import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { showToastNotification } from "../../../App/state/actions";
import { loadSeasons } from "../state/actions";
import { getAutoGeneratedObject, getAutogeneratedEpisodeString } from "../../../shared/utils";
import { isItemTypeEpisode } from "../../../shared/utils/content";
import { getEpisodeDetail } from "../../../shared/utils/swimlane";
import { createMetadataInfo } from "../../../shared/utils/playbackHelpers";
import { setDTPageAttributes } from "../../../shared/analytics/dynatrace";
import { useReducers } from "../../../shared/hooks/useReducer";
import { DT_ATTRIBUTES } from "../../../shared/constants/dynatrace";
import constants from "../../../shared/constants";

const { REDUCER_TYPE } = constants;

const useContentMetadata = ({
  isLivePlayer,
  programContent,
  setCurrentProgram,
  isRecordingPlayer,
  stopDeltaTime,
  currentEpisodeId,
  isVodPlayer,
  setMetadataInfo,
  setPageTitle,
  isProgramChangedRef,
  triggerAnalytics,
  appProvider,
  contentSubtype,
  programDetailsRef,
  liveProgramDetails,
  isRecordingEnabled,
  toastNotificationTimer,
  sportEventRecordingData,
}) => {
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isDynatraceTrackingEnabled } = featureToggles;
  const seriesDetails = useSelector(({ onDemandPlayer }) => onDemandPlayer.seriesDetails);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  useEffect(() => {
    if (!isLivePlayer && programContent && programContent.length) {
      programDetailsRef.current = programContent;
      let title = "";
      let subTitle = "";
      let deltaTime = null;
      let selectedProgram = null;
      if (programContent.length > 1) {
        const { isSportRecording, isTeamRecording, recordingSeriesId } = sportEventRecordingData || {};
        let filteredContent = null;
        if ((isSportRecording || isTeamRecording) && recordingSeriesId) {
          filteredContent = programContent.find((content) => content.metadata.recordingSeriesId == recordingSeriesId);
        }
        if (!filteredContent) {
          filteredContent = programContent.find((content) => content.metadata.stopDeltaTime == stopDeltaTime);
        }
        selectedProgram = filteredContent || null;
      } else if (programContent.length === 1) {
        selectedProgram = programContent[0];
      }
      setCurrentProgram(selectedProgram);

      const programMetadata = selectedProgram?.metadata;
      const extendedMetadata = programMetadata?.extendedMetadata ?? null;
      if (programMetadata) {
        const uaEpisodeObject = programMetadata && getAutoGeneratedObject(programMetadata);
        title = programMetadata.title;
        if (programMetadata.contentSubtype === "EPISODE" || (programMetadata.season && programMetadata.episodeNumber)) {
          subTitle = getAutogeneratedEpisodeString(uaEpisodeObject, programMetadata);
          if (extendedMetadata) {
            extendedMetadata.season = programMetadata.season;
            extendedMetadata.episodeNumber = programMetadata.episodeNumber;
          }
          setDTPageAttributes({ [DT_ATTRIBUTES.PROGRAM_NAME]: subTitle }, isDynatraceTrackingEnabled);
        } else {
          setDTPageAttributes({ [DT_ATTRIBUTES.PROGRAM_NAME]: title }, isDynatraceTrackingEnabled);
        }
        /* We need to use recordingStartTime, startDeltaTime and stopDeltaTime to determine the user specific recording's playable duration and start time */
        if (
          isRecordingPlayer &&
          programMetadata.recordingStartTime !== undefined &&
          programMetadata.startDeltaTime !== undefined &&
          programMetadata.stopDeltaTime !== undefined
        ) {
          deltaTime = {
            recordingStartTime: programMetadata.recordingStartTime,
            startDeltaTime: programMetadata.startDeltaTime,
            stopDeltaTime: programMetadata.stopDeltaTime,
            programStartTime: programMetadata.programStartTime,
          };
        }
      }
      title = title ? title : currentEpisodeId;
      const duration = isVodPlayer ? 1280 : programMetadata?.programDuration ?? 0;
      const startTime = isVodPlayer ? {} : { programStartTime: programMetadata?.programStartTime ?? 0 };
      setMetadataInfo(
        createMetadataInfo(title, subTitle, null, duration, startTime, null, "", extendedMetadata, deltaTime)
      );
      setPageTitle(title);

      if (isProgramChangedRef.current) triggerAnalytics();
    }
  }, [
    appProvider,
    contentSubtype,
    currentEpisodeId,
    isLivePlayer,
    isRecordingPlayer,
    isVodPlayer,
    programContent,
    translate,
    triggerAnalytics,
    isProgramChangedRef,
    programDetailsRef,
    setCurrentProgram,
    setMetadataInfo,
    setPageTitle,
    stopDeltaTime,
    isDynatraceTrackingEnabled,
    sportEventRecordingData,
  ]);

  useEffect(() => {
    if (!isLivePlayer && seriesDetails && seriesDetails.metadata?.uaType !== "MOVIE") {
      if (seriesDetails.containers?.length > 0) {
        const sortedSeasonContainer = [...seriesDetails.containers]?.sort(
          (previousSeason, nextSeason) => nextSeason.metadata.season - previousSeason.metadata.season
        );
        dispatch(loadSeasons(appProvider, seriesDetails.id, sortedSeasonContainer));
      }
      if (programContent?.[0]?.metadata?.contentSubtype === "EPISODE") {
        const metadataTitle = seriesDetails.title || programContent?.[0]?.metadata?.extendedMetadata?.dlum?.sortTitle;
        setPageTitle(metadataTitle);
        setMetadataInfo((metadataInfo) => ({ ...metadataInfo, title: metadataTitle }));
        setDTPageAttributes({ [DT_ATTRIBUTES.SERIES_NAME]: metadataTitle }, isDynatraceTrackingEnabled);
      }
    }
  }, [
    seriesDetails,
    appProvider,
    dispatch,
    programContent,
    isLivePlayer,
    setMetadataInfo,
    setPageTitle,
    isDynatraceTrackingEnabled,
  ]);

  useEffect(() => {
    if (isLivePlayer && liveProgramDetails) {
      const channel = liveProgramDetails.channel;
      const programMetadata = liveProgramDetails.metadata;
      let channelLogo = "",
        channelNumber;
      if (channel) {
        channelLogo = channel.assets?.[0]?.logoSmall ?? "";
        channelNumber = channel.number;

        if (isRecordingEnabled && !liveProgramDetails?.metadata?.isRecordable) {
          toastNotificationTimer.current = setTimeout(() => {
            dispatch(showToastNotification(translate("error_program_not_available_recording")));
          }, 2000);
        }
      }
      setMetadataInfo(createMetadataInfo(programMetadata?.title ?? "", "", channelLogo, 0, {}, channelNumber));
      setPageTitle(programMetadata?.title ?? "");
      if (programMetadata) {
        if (isItemTypeEpisode(programMetadata)) {
          setDTPageAttributes({ [DT_ATTRIBUTES.SERIES_NAME]: programMetadata.title }, isDynatraceTrackingEnabled);
          setDTPageAttributes(
            { [DT_ATTRIBUTES.PROGRAM_NAME]: getEpisodeDetail(programMetadata) },
            isDynatraceTrackingEnabled
          );
        } else {
          setDTPageAttributes({ [DT_ATTRIBUTES.PROGRAM_NAME]: programMetadata.title }, isDynatraceTrackingEnabled);
        }
      }
    }
  }, [
    isLivePlayer,
    appProvider.channelMapID,
    isRecordingEnabled,
    liveProgramDetails,
    dispatch,
    translate,
    setMetadataInfo,
    setPageTitle,
    toastNotificationTimer,
    isDynatraceTrackingEnabled,
  ]);
};

export default useContentMetadata;
